import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import GlobalFunctions from "../Services/GlobalFunctions";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("jwt_token"));
  const [isAuthenticated, setIsAuthenticated] = useState(!!token);
  const API_URL = process.env.REACT_APP_API_URL;
  const glob = new GlobalFunctions();

  axios.defaults.baseURL = API_URL;

 useEffect(() => {
    if (token) {
      // Configura el token antes de cualquier solicitud
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      axios.post("/auth/me")
        .then((response) => {
          // Si la solicitud es exitosa, el token es válido
          const fechaAhora = glob.getFecha();
          glob.setCookie("fechaUltimoToken", fechaAhora, 3600 * 24);
          setUser(response.data);
          setIsAuthenticated(true);
        })
        .catch(async (error) => {
          // Si hay un error, lo primero es verificar si es un 401 (Unauthorized)
          if (error.response && error.response.status === 401) {
            try {
              // Llama a la función de refresco
              await refresh(); 
              // Si el refresh fue exitoso, vuelve a cargar los datos del usuario
              const response = await axios.post("/auth/me");
              const fechaAhora = glob.getFecha();
              glob.setCookie("fechaUltimoToken", fechaAhora, 3600 * 24);
              setUser(response.data);
              setIsAuthenticated(true);
            } catch (refreshError) {
              // Si el refresh falla, el método `refresh` ya se encargó de llamar a `logout`
            }
          } else {
            // Manejo de errores que no sean 401 (por ejemplo, sin conexión)
            let fechaUltimoToken = glob.getCookie("fechaUltimoToken");
            if (fechaUltimoToken && fechaUltimoToken !== glob.getFecha()) {
              logout();
            }
          }
        });
    } else {
      // No hay token, asegúrate de que el estado esté limpio
      delete axios.defaults.headers.common["Authorization"];
      setUser(null);
      setIsAuthenticated(false);
    }
  }, [token]);

  const login = async (email, password) => {
    try {
      const response = await axios.post("/auth/login", { email, password });
      const newToken = response.data.access_token;
      localStorage.setItem("jwt_token", newToken);
      setToken(newToken);
      setIsAuthenticated(true);
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      // Opcional: Notificar al backend sobre el logout si quieres invalidar el token inmediatamente
      if (token) {
        const logout = await axios.post("/auth/logout");
      }
    } catch (error) {
    } finally {
      localStorage.removeItem("jwt_token");
      setToken(null);
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  const refresh = async () => {
    try {
      // Usamos el token actual para refrescar
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`; 
      const response = await axios.post("/auth/refresh");
      const newToken = response.data.access_token;
      localStorage.setItem("jwt_token", newToken);
      setToken(newToken);
      // Actualizamos el header de Axios con el nuevo token
      axios.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
      return response.data;
    } catch (error) {
      // Si el refresh falla (ej. el refresh token también expiró),
      // debemos cerrar la sesión.
      logout();
      throw error;
    }
  };

  return (
    <AuthContext.Provider
      value={{ user, isAuthenticated, login, logout, refresh, token }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
